// Libraries
import React, {forwardRef} from 'react';

// Supermove
import {DataLoader, Form, Icon, Space, Styled} from '@supermove/components';
import {DocumentNode, gql, useQuery} from '@supermove/graphql';
import {
  ChargeAmountKind,
  DocumentItemInputChargePaymentType,
  DocumentModel,
  OrganizationModel,
  PaymentMethod,
  PaymentMethodModel,
  ProjectModel,
} from '@supermove/models';
import {colors} from '@supermove/styles';
import {Currency, Percent} from '@supermove/utils';

// App
import FieldInput from '@shared/design/components/Field/FieldInput';
import Text from '@shared/design/components/Text';
import TextTooltip from '@shared/design/components/TextTooltip';
import DocumentV2ChargeClientCreditCard, {
  DocumentV2ChargeClientCardHandle,
} from '@shared/modules/Document/components/DocumentV2ChargeClientCreditCard';

const TooltipContainer = Styled.View``;

const LoadingIndicator = Styled.Loading`
`;

const Row = Styled.View`
  flex-direction: row;
  align-items: center;
`;

const DocumentV2ChargePaymentInputNoPaymentMethod = ({
  organization,
}: {
  organization?: OrganizationModel;
}) => {
  return (
    <React.Fragment>
      <Text.Body>
        Please contact {organization?.name || 'the office'} to make your payment.
      </Text.Body>
    </React.Fragment>
  );
};

export const convertAmountKindAndAmountToText = (
  amountKind: ChargeAmountKind,
  amountFixedInCents: number | undefined,
  amountPercentageInFraction: number | undefined,
) => {
  switch (amountKind) {
    case ChargeAmountKind.FIXED:
      return amountFixedInCents ? Currency.display(amountFixedInCents) : `TBD`;
    case ChargeAmountKind.PERCENT_OF_BALANCE:
      return amountPercentageInFraction
        ? `${Percent.display(amountPercentageInFraction)} of Balance`
        : `TBD`;
    default:
      return `TBD`;
  }
};

const convertAmountKindAndAmountToTotalAmount = (
  amountKind: ChargeAmountKind,
  amountFixedInCents: number | undefined,
  amountPercentageInFraction: number | undefined,
  balance: number,
): number => {
  switch (amountKind) {
    case ChargeAmountKind.FIXED:
      return amountFixedInCents ?? 0;
    case ChargeAmountKind.PERCENT_OF_BALANCE:
      return Math.max(balance * (amountPercentageInFraction ?? 0), 0);
    default:
      return 0;
  }
};

const DocumentV2ChargePaymentInputBodyView = ({
  value,
  paymentMethodName,
  cardSummaryName,
}: {
  value: DocumentItemInputChargePaymentType['input']['value'];
  paymentMethodName: string;
  cardSummaryName: string;
}) => {
  return (
    <React.Fragment>
      <Text.Subheading>Payment Method</Text.Subheading>
      <Space height={8} />
      <Row>
        <Text.Label>{paymentMethodName}</Text.Label>
        <Space width={32} />
        <Text.Body>{cardSummaryName}</Text.Body>
      </Row>
      <Space height={8} />
      <Row>
        <Text.Label>Payment Total</Text.Label>
        <Space width={32} />
        <Text.Body>
          {Currency.display(value.totalAmountInCents)}
          {value.feeInCents > 0 && (
            <React.Fragment>
              {' '}
              + <Text.Body>{value.feeDisplay}</Text.Body>
            </React.Fragment>
          )}
        </Text.Body>
      </Row>
    </React.Fragment>
  );
};

const DocumentV2ChargePaymentInputBodyEdit = forwardRef<
  DocumentV2ChargeClientCardHandle,
  {
    form: Form;
    field: string;
    isRequired: boolean;
    paymentMethod: PaymentMethodModel;
    project: ProjectModel;
    documentName: string;
    input: DocumentItemInputChargePaymentType['input'];
  }
>(({form, field, isRequired, paymentMethod, project, documentName, input}, ref) => {
  if (paymentMethod.kind === 'PAYENGINE_CREDIT_CARD') {
    const amountInCents = Math.max(
      convertAmountKindAndAmountToTotalAmount(
        input.metadata.amountKind,
        input.metadata.amountFixedInCents,
        input.metadata.amountPercentageInFraction,
        project.mainMoveInvoice.remainingBalance,
      ),
      0,
    );
    const feeAmountInCents = PaymentMethod.computePaymentFeeAmount(paymentMethod, {
      amount: amountInCents,
    });
    const feeDisplay = PaymentMethod.getPaymentMethodDisplay(paymentMethod);

    return (
      <React.Fragment>
        <Text.Subheading>Credit Card Information</Text.Subheading>
        <Space height={8} />
        <FieldInput.LabelText isResponsive isRequired={isRequired}>
          Payment Method
        </FieldInput.LabelText>
        <Space height={4} />
        <DocumentV2ChargeClientCreditCard
          ref={ref}
          clientId={project.client.id}
          project={project}
          documentName={documentName}
          allowSelectExistingPaymentMethods={input.metadata.allowSelectExistingPaymentMethods}
          paymentMethod={paymentMethod}
          amountInCents={amountInCents}
          feeAmountInCents={feeAmountInCents}
          onPaymentSuccess={({paymentId, cardSummaryName}) => {
            form.setFieldValue(`${field}.input.value.paymentId`, paymentId);
            form.setFieldValue(`${field}.input.value.paymentMethodName`, paymentMethod.name);
            form.setFieldValue(`${field}.input.value.cardSummaryName`, cardSummaryName);
            form.setFieldValue(`${field}.input.value.totalAmountInCents`, amountInCents);
            form.setFieldValue(`${field}.input.value.amountInCents`, amountInCents);
            form.setFieldValue(`${field}.input.value.feeInCents`, feeAmountInCents);
            form.setFieldValue(`${field}.input.value.feeDisplay`, feeDisplay);
          }}
        />
        <Space height={8} />
        <Row>
          <TextTooltip text={'This payment method has an addition fee.'} placement='top'>
            <TooltipContainer>
              <Icon source={Icon.CircleInfo} size={16} color={colors.gray.secondary} />
            </TooltipContainer>
          </TextTooltip>
          <Space width={8} />
          <Text.Label>Payment Total</Text.Label>
          <Space width={32} />
          <Text.Label>
            {Currency.display(amountInCents)}
            {feeAmountInCents > 0 && <React.Fragment> + {feeDisplay}</React.Fragment>}
          </Text.Label>
        </Row>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Text.Subheading>Payment Method</Text.Subheading>
      <Space height={8} />
      <Text.Body>Please contact {project.organization.name} to make your payment.</Text.Body>
    </React.Fragment>
  );
});

type DocumentV2ChargePaymentInputEditProps = {
  input: DocumentItemInputChargePaymentType['input'];
  form: Form;
  field: string;
  isEditable: boolean;
  isRequired: boolean;
  document: DocumentModel;
};

type DocumentV2ChargePaymentInputEditComponent = React.ForwardRefExoticComponent<
  DocumentV2ChargePaymentInputEditProps & React.RefAttributes<DocumentV2ChargeClientCardHandle>
> & {
  fragment: DocumentNode;
  query: DocumentNode;
};

const DocumentV2ChargePaymentInputEdit = forwardRef<
  DocumentV2ChargeClientCardHandle,
  DocumentV2ChargePaymentInputEditProps
>(({input, form, field, isEditable, isRequired, document}, ref) => {
  const {paymentMethodIds} = input.metadata;
  const paymentMethodId = paymentMethodIds.length > 0 ? paymentMethodIds[0] : null;

  const project = document?.job ? document.job.project : document?.invoice.project;

  const {data, loading} = useQuery<{
    project: ProjectModel;
  }>(DocumentV2ChargePaymentInputEdit.query, {
    variables: {projectUuid: project.uuid, paymentMethodId},
    skip: !paymentMethodId || !project,
  });

  if (!paymentMethodId) {
    return <DocumentV2ChargePaymentInputNoPaymentMethod organization={project.organization} />;
  }

  if (!isEditable) {
    return (
      <DocumentV2ChargePaymentInputBodyView
        value={input.value}
        paymentMethodName={input.value.paymentMethodName}
        cardSummaryName={input.value.cardSummaryName}
      />
    );
  }

  return (
    <DataLoader data={data} loading={loading} as={LoadingIndicator}>
      {({loadedData}) =>
        loadedData?.project.organization.paymentMethod ? (
          <DocumentV2ChargePaymentInputBodyEdit
            ref={ref}
            input={input}
            form={form}
            field={field}
            isRequired={isRequired}
            paymentMethod={loadedData.project.organization.paymentMethod}
            project={project}
            documentName={document.displayName}
          />
        ) : (
          <DocumentV2ChargePaymentInputNoPaymentMethod organization={project.organization} />
        )
      }
    </DataLoader>
  );
}) as DocumentV2ChargePaymentInputEditComponent;

DocumentV2ChargePaymentInputEdit.fragment = gql`
  ${DocumentV2ChargeClientCreditCard.fragment}

  fragment DocumentV2ChargePaymentInputEdit_Project on Project {
    id
    uuid
    client {
      id
    }
    organization {
      id
      name
    }
    mainMoveInvoice {
      id
      remainingBalance
    }
  }

  fragment DocumentV2ChargePaymentInputEdit_Document on Document {
    id
    job {
      id
      project {
        id
        ...DocumentV2ChargePaymentInputEdit_Project
      }
    }
    invoice {
      id
      project {
        id
        ...DocumentV2ChargePaymentInputEdit_Project
      }
    }
    displayName
    ...DocumentV2ChargeClientCreditCard_Document
  }
`;

// Payment items rely on payment method id, so we need a separate query
DocumentV2ChargePaymentInputEdit.query = gql`
  ${PaymentMethod.computePaymentFeeAmount.fragment}
  ${PaymentMethod.getPaymentMethodDisplay.fragment}

  query DocumentV2ChargePaymentInputEdit($projectUuid: String!, $paymentMethodId: Int!) {
    project(uuid: $projectUuid) {
      id
      organization {
        id
        paymentMethod(paymentMethodId: $paymentMethodId) {
          id
          kind
          name
          ...PaymentMethod_computePaymentFeeAmount
          ...PaymentMethod_getPaymentMethodDisplay
        }
      }
    }
  }
`;

export default DocumentV2ChargePaymentInputEdit;
